/**
 * Gallery tile presentation helpers.
 *
 * The tile list itself is NOT here. Tiles are theme rows (`hobbies` with
 * `tile_active`) and reach the client through `get_public_program_by_slug`
 * (`program.styles`: slug, name, emoji, category, colour, picture, theme_id,
 * sub_style_id). This module keeps only the category vocabulary and the colour
 * fallback — nothing that could drift from the theme table.
 */
export type StyleCategory = "sports" | "pets" | "party" | "life";

export const STYLE_CATEGORIES: { id: StyleCategory | "all"; label: string }[] = [
  { id: "all", label: "All" },
  { id: "sports", label: "Sports" },
  { id: "pets", label: "Pets" },
  { id: "party", label: "Party" },
  { id: "life", label: "Life" },
];

/** Fallback colourway (Grape) when a tile row carries none. */
export const FALLBACK_TILE_COLOR = "#8834B2";

export interface TileMeta {
  slug: string;
  name: string;
  category: StyleCategory;
  color: string;
  art: string | null;
  themeId: string | null;
  subStyleId: string | null;
}

/** Normalise a programme tile row (from `get_public_program_by_slug`) into presentation meta. */
export function tileMeta(row: {
  style_id: string;
  display_name?: string | null;
  category?: string | null;
  color?: string | null;
  preview_image_url?: string | null;
  theme_id?: string | null;
  sub_style_id?: string | null;
}): TileMeta {
  return {
    slug: row.style_id,
    name: row.display_name || row.style_id,
    category: (row.category as StyleCategory) || "life",
    color: row.color || FALLBACK_TILE_COLOR,
    art: row.preview_image_url ?? null,
    themeId: row.theme_id ?? null,
    subStyleId: row.sub_style_id ?? null,
  };
}

/** Find a tile by slug in a programme's tile list. */
export function findTile(
  tiles: Array<Parameters<typeof tileMeta>[0]> | null | undefined,
  slug: string | null | undefined,
): TileMeta | undefined {
  if (!slug || !tiles) return undefined;
  const row = tiles.find((t) => t.style_id === slug);
  return row ? tileMeta(row) : undefined;
}

/** Colour for a slug within a programme, falling back to Grape. */
export function tileColor(
  tiles: Array<Parameters<typeof tileMeta>[0]> | null | undefined,
  slug: string | null | undefined,
): string {
  return findTile(tiles, slug)?.color || FALLBACK_TILE_COLOR;
}
